import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import KennelDefaultContractCard from "@/components/KennelDefaultContractCard";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { FileTextIcon as FileText, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle, ClockIcon as Clock, WarningIcon as Warning, PawPrintIcon as PawPrint, ArrowSquareOutIcon as ArrowSquareOut, PenNibIcon as PenNib } from "@phosphor-icons/react";

/**
 * Dedicated /contracts hub page. The single home for everything
 * contract-related so breeders + buyers don't have to hunt for it.
 *
 * Sections:
 *   1. Status strip (counts: awaiting your signature, awaiting buyer,
 *      fully executed)
 *   2. Kennel default contract (template uploader, breeders only)
 *   3. Contracts table — one row per contract the user is party to,
 *      hydrated with dog name, status pill, and a per-status CTA
 *
 * Buyer-side surfaces a HUGE "Sign now" call to action on every
 * breeder_signed contract assigned to them. Breeder-side surfaces
 * "Sign as breeder" on every draft for puppies they own.
 */
export default function ContractsHub() {
  const [rows, setRows] = useState(null);
  const [signingId, setSigningId] = useState("");
  const [me, setMe] = useState(null);

  const load = async () => {
    try {
      const [c, u] = await Promise.all([
        api.get("/contracts/mine"),
        api.get("/auth/me", { silent: true }).catch(() => null),
      ]);
      setRows(Array.isArray(c.data) ? c.data : []);
      setMe(u?.data || null);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  if (rows === null) return <AppShell><div className="max-w-5xl mx-auto px-4 py-10">Loading…</div></AppShell>;

  const myUserId = me?.id;
  const myEmail = (me?.email || "").toLowerCase();

  const myRole = (r) => {
    // "breeder" if I own the dog row; "buyer" if I'm the buyer side;
    // "admin" handled by backend already (admin still resolves to one side).
    if (r.owner_user_id === myUserId) return "breeder";
    if (r.buyer_user_id === myUserId) return "buyer";
    if ((r.buyer_email || "").toLowerCase() === myEmail) return "buyer";
    return "other";
  };

  const counts = {
    awaiting_me_breeder: rows.filter((r) => myRole(r) === "breeder" && r.status === "draft").length,
    awaiting_me_buyer: rows.filter((r) => myRole(r) === "buyer" && r.status === "breeder_signed").length,
    awaiting_buyer: rows.filter((r) => myRole(r) === "breeder" && r.status === "breeder_signed").length,
    executed: rows.filter((r) => r.status === "fully_executed").length,
  };

  const totalActionable = counts.awaiting_me_breeder + counts.awaiting_me_buyer;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="contracts-hub">
        <header className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <PenNib size={22} weight="duotone" className="text-crimson" />
            <h1 className="font-spectral text-2xl sm:text-3xl tracking-tight">Contracts</h1>
          </div>
          <p className="text-[13px] sm:text-sm text-[#6B6B6B] max-w-2xl">
            Upload your kennel default contract once, then attach it to every puppy you list.
            Both sides click-wrap sign in the browser — no DocuSign, no printing, no faxing.
            ESIGN Act compliant, IP + timestamp + legal-name captured on every signature.
          </p>
        </header>

        {/* Status strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="contracts-status-strip">
          <StatusTile
            color={totalActionable > 0 ? "crimson" : "ink"}
            icon={Clock}
            value={totalActionable}
            label="Awaiting your signature"
            testid="status-awaiting-me"
          />
          <StatusTile
            color="gold"
            icon={Warning}
            value={counts.awaiting_buyer}
            label="Awaiting buyer signature"
            testid="status-awaiting-buyer"
          />
          <StatusTile
            color="green"
            icon={CheckCircle}
            value={counts.executed}
            label="Fully executed"
            testid="status-executed"
          />
          <StatusTile
            color="ink"
            icon={FileText}
            value={rows.length}
            label="Total contracts"
            testid="status-total"
          />
        </div>

        {/* Kennel default — the prominent "where do I upload?" answer */}
        <section className="mb-8" data-testid="kennel-default-section">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="font-spectral text-xl tracking-tight">Your kennel default contract</h2>
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#6B6B6B]">Template</span>
          </div>
          <p className="text-[12.5px] text-[#6B6B6B] mb-3 max-w-2xl">
            Upload your reusable sales-agreement template here. We'll auto-attach it to every
            new puppy you list — you can override per-puppy from the dog detail page if needed.
          </p>
          <KennelDefaultContractCard />
        </section>

        {/* Contracts table */}
        <section data-testid="contracts-list-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-spectral text-xl tracking-tight">Per-puppy contracts</h2>
            <Link
              to="/dogs"
              className="text-[12px] font-bold text-ink hover:text-crimson inline-flex items-center gap-1"
            >
              Go to your dogs <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          {rows.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="space-y-2" data-testid="contracts-list">
              {rows.map((r) => (
                <ContractRow
                  key={r.id}
                  row={r}
                  role={myRole(r)}
                  busy={signingId === r.id}
                  onSign={async () => {
                    setSigningId(r.id);
                    try {
                      // Drop the user on the dog detail page where the
                      // existing SaleContractCard already houses the
                      // click-wrap signature modal (legal name + venue
                      // disclaimer). No need to duplicate the form here.
                      window.location.href = `/dogs/${r.dog_id}#contract`;
                    } finally {
                      setSigningId("");
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}

function StatusTile({ color, icon: Icon, value, label, testid }) {
  const cls = {
    crimson: "bg-[#FFF0F0] border-crimson/40 text-crimson",
    gold: "bg-[#FBF7EC] border-[#E8D9A6] text-gold-dark",
    green: "bg-[#EEFBEC] border-[#BFE8C9] text-[#0F8043]",
    ink: "bg-white border-[#EAEAEA] text-ink",
  }[color];
  return (
    <div className={`rounded-xl border-2 ${cls} p-3 flex flex-col items-start gap-1 min-w-0`} data-testid={testid}>
      <Icon size={16} weight="duotone" />
      <span className="font-spectral text-2xl sm:text-3xl tabular-nums leading-none text-ink">{value}</span>
      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B6B6B] leading-tight">{label}</span>
    </div>
  );
}

function ContractRow({ row, role, busy, onSign }) {
  const status = row.status;
  const statusMeta = {
    draft: { label: "Draft", cls: "bg-[#FBF7EC] text-gold-dark border-[#E8D9A6]" },
    breeder_signed: { label: "Breeder signed", cls: "bg-[#EEF2FF] text-[#3A47B2] border-[#C7D2FE]" },
    fully_executed: { label: "Fully executed", cls: "bg-[#EEFBEC] text-[#0F8043] border-[#BFE8C9]" },
  }[status] || { label: status, cls: "bg-white text-ink border-[#EAEAEA]" };

  let cta = null;
  if (role === "breeder" && status === "draft") {
    cta = (
      <Button onClick={onSign} disabled={busy} size="sm" className="rounded-full bg-ink hover:bg-crimson text-white whitespace-nowrap" data-testid={`contract-sign-breeder-${row.id}`}>
        Sign as breeder <ArrowRight size={12} weight="bold" className="ml-1" />
      </Button>
    );
  } else if (role === "buyer" && status === "breeder_signed") {
    cta = (
      <Button onClick={onSign} disabled={busy} size="sm" className="rounded-full bg-crimson hover:bg-ink text-white whitespace-nowrap" data-testid={`contract-sign-buyer-${row.id}`}>
        Sign now <PenNib size={12} weight="bold" className="ml-1" />
      </Button>
    );
  } else if (role === "breeder" && status === "breeder_signed") {
    cta = (
      <span className="text-[11px] text-[#6B6B6B] italic whitespace-nowrap">
        Waiting on buyer{row.buyer_email ? ` (${row.buyer_email})` : ""}
      </span>
    );
  }

  return (
    <li className="bg-white border border-[#EAEAEA] rounded-xl p-4 hover:border-ink/40 transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`contract-row-${row.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="p-2 rounded-full bg-[#FBF7EC] shrink-0">
          <PawPrint size={16} weight="duotone" className="text-gold-dark" />
        </span>
        <div className="min-w-0">
          <Link to={`/dogs/${row.dog_id}#contract`} className="text-sm font-bold text-ink hover:text-crimson truncate block">
            {row.dog_name || "Untitled puppy"}
            {row.dog_registration_number && (
              <span className="text-[11px] text-[#6B6B6B] font-normal ml-2">{row.dog_registration_number}</span>
            )}
          </Link>
          <p className="text-[11px] text-[#6B6B6B] truncate">
            {role === "buyer"
              ? <>Owner: {row.owner_name || row.owner_email || "—"}</>
              : <>Buyer: {row.buyer_email || "not assigned yet"}</>}
            {" · "}
            {row.uploaded_at ? `Uploaded ${new Date(row.uploaded_at).toLocaleDateString()}` : "—"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap sm:shrink-0 pl-12 sm:pl-0">
        {row.document_url && (
          <a
            href={row.document_url}
            target="_blank"
            rel="noreferrer"
            className="text-[12px] text-ink hover:text-crimson inline-flex items-center gap-1"
            data-testid={`contract-open-${row.id}`}
          >
            PDF <ArrowSquareOut size={11} weight="bold" />
          </a>
        )}
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${statusMeta.cls} whitespace-nowrap`}>
          {statusMeta.label}
        </span>
        {cta}
      </div>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border-2 border-dashed border-[#EAEAEA] p-8 text-center" data-testid="contracts-empty">
      <FileText size={28} weight="duotone" className="text-[#9B9B9B] mx-auto mb-2" />
      <h3 className="font-spectral text-lg">No contracts yet</h3>
      <p className="text-[12.5px] text-[#6B6B6B] mt-1 max-w-md mx-auto">
        Open a puppy's detail page and use the <strong>Sale contract</strong> card to upload
        one. If you've uploaded a kennel default above, it auto-attaches.
      </p>
      <Link to="/dogs">
        <Button size="sm" className="rounded-full bg-ink hover:bg-crimson text-white mt-4">
          Go to your dogs <ArrowRight size={12} weight="bold" className="ml-1" />
        </Button>
      </Link>
    </div>
  );
}
